import type { ScreenContext } from '../../shared';
import { haptic, type HapticId } from '../../ui';
import {isSuperAction,type DuelBeat} from '../sim';
import { duelTiming } from './duelPresentation';
import {isSuperAction as isWeaponAbility} from '../../shared/weapon-motion';
import {terminalTiming} from './duelTerminal';

type DuelSound = 'windup'|'hit_light'|'hit_heavy'|'prayer'|'ko'|'ui_seal';
interface FeedbackPort {
  play(id:DuelSound,opts:{atMs:number;gain:number}):void;
  pulse(id:HapticId):void;
}
interface Cue {at:number;sound:DuelSound;gain:number;pulse?:HapticId}

/** Emit at the displayed contact, so pausing, leaving or hiding cannot leave future audio behind. */
export function createDuelFeedback(port:FeedbackPort) {
  let cues:Cue[]=[];
  return {
    hit(beat:DuelBeat,at:number,winner:0|1|null,reduced=false) {
      const timing=duelTiming(beat);
      cues=[{at,sound:'windup',gain:beat.action==='prayer'?.22:.38}];
      if(winner!==null){const end=terminalTiming(beat,winner,reduced);cues.push({at:at+end.landAt,sound:'hit_heavy',gain:.3},{at:at+end.celebrateAt,sound:'ui_seal',gain:.6});}
      if(beat.healed>0)cues.push({at:at+timing.heroContact.at(-1)!,sound:'prayer',gain:.7});
      if(beat.fervorTaken)cues.push({at:at+timing.enemyContact,sound:'ui_seal',gain:.5,pulse:'confirm'});
      if(beat.action==='guard') {
        for(const [i,contact] of timing.heroContact.entries()) {
          const final=winner!==null&&i===timing.heroContact.length-1;
          cues.push({at:at+contact,sound:final?'ko':'ui_seal',gain:final?.45:.65,pulse:final?'ko':'confirm'});
        }
        if(beat.foeHealed)cues.push({at:at+timing.enemyContact,sound:'prayer',gain:.6});
        cues.sort((a,b)=>a.at-b.at);return;
      }
      if(beat.dealt===0&&beat.armourBlocked)cues.push({at:at+timing.heroContact[0],sound:'ui_seal',gain:.5,pulse:'confirm'});
      if(beat.dealt>0)for(const [i,contact] of timing.heroContact.entries()) {
        const final=winner===0&&i===timing.heroContact.length-1;
        const rapid=timing.heroContact.length>10&&i>0&&i<timing.heroContact.length-1;
        const authored=isWeaponAbility(beat.action),last=i===timing.heroContact.length-1;
        const light=rapid||beat.action==='shadowDance'&&!last||beat.action==='tempest'&&!last;
        cues.push({at:at+contact,sound:final?'ko':light?'hit_light':authored||isSuperAction(beat.action)||beat.weapon==='hammer'?'hit_heavy':'hit_light',gain:final?.45:rapid?.2:light?.4:.65,
          pulse:final?'ko':authored&&(i===0||last)?'crit':isSuperAction(beat.action)&&!rapid?'crit':undefined});
      }
      if(winner!==0) {
        if(!beat.noReply&&(beat.techniqueBlocked||beat.shieldBlocked))for(const contact of timing.enemyContacts)cues.push({at:at+contact,sound:'ui_seal',gain:.55,pulse:'confirm'});
        if(beat.foeHealed)cues.push({at:at+timing.enemyContact,sound:'prayer',gain:.6});
        if(beat.received>0)for(const contact of timing.enemyContacts)cues.push({at:at+contact,sound:winner===1&&contact===timing.enemyContact?'ko':beat.heavy?'hit_heavy':'hit_light',gain:.65,
          pulse:winner===1&&contact===timing.enemyContact?'ko':beat.heavy?'hit_taken_heavy':undefined});
      }
      cues.sort((a,b)=>a.at-b.at);
    },
    render(ms:number) {
      while(cues.length&&cues[0].at<=ms) {
        const cue=cues.shift()!;
        // A resumed tab or a seek past the exchange must not replay a burst of old contacts.
        if(ms-cue.at>160)continue;
        port.play(cue.sound,{atMs:cue.at,gain:cue.gain});if(cue.pulse)port.pulse(cue.pulse);
      }
    },
    dispose(){cues=[];},
  };
}

export function duelFeedback(ctx:ScreenContext) {
  // App-owned audio is an optional presentation service, also absent from simulation test contexts.
  const audio=(ctx as ScreenContext & {audio?:Pick<FeedbackPort,'play'>}).audio;
  return createDuelFeedback({play:(id,opts)=>audio?.play(id,opts),pulse:id=>{haptic(id);}});
}
